import React, { useCallback, useEffect, useState } from "react";
import "./questions-form.component.scoped.scss";
import { Box, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AppButtonComponent from "../../../../../components/form/app-button";
import AppSwitchComponent from "../../../../../components/form/app-switch";
import AppCheckboxComponent from "../../../../../components/form/app-checkbox";
import { icons } from "../../../../../statics/icons";
import { services } from "../../../../../services";
import { showPopupToastMsg, showToastMsg } from "../../../../../helpers";
import { Skeleton } from "@material-ui/lab";
import { TFunction } from "i18next";

const FIELD_LIMITS = {
  QUESTION: 250,
  ANSWER: 400,
  MIN_ANSWERS: 2,
} as const;

const emptyAnswerForm = () => ({
  text: "",
  isCorrect: false,
});

const emptyQuestionForm = () => ({
  visibility: "PUBLIC",
  question: "",
  answers: [emptyAnswerForm(), emptyAnswerForm()],
});

interface AnswerFieldErrors {
  text?: string;
}

interface QuestionFieldErrors {
  question?: string;
  answersGeneral?: string;
  answers: AnswerFieldErrors[];
}

interface QuestionsFormErrors {
  form?: string;
  questions: QuestionFieldErrors[];
}

const createEmptyQuestionErrors = (): QuestionFieldErrors => ({
  answers: [],
});

const validateQuestionsForm = (
  questions: any[],
  t: TFunction,
): { valid: boolean; errors: QuestionsFormErrors } => {
  if (!questions.length) {
    return {
      valid: false,
      errors: {
        form: t("EDUCATION_PAGE.AT_LEAST_ONE_QUESTION_REQUIRED"),
        questions: [],
      },
    };
  }

  let valid = true;
  const questionErrors: QuestionFieldErrors[] = questions.map((question) => {
    const qErr = createEmptyQuestionErrors();
    const trimmedQuestion = (question.question ?? "").trim();

    if (!trimmedQuestion) {
      qErr.question = t("EDUCATION_PAGE.QUESTION_TEXT_REQUIRED");
      valid = false;
    } else if (trimmedQuestion.length > FIELD_LIMITS.QUESTION) {
      qErr.question = t("EDUCATION_PAGE.QUESTION_TEXT_TOO_LONG", {
        max: FIELD_LIMITS.QUESTION,
      });
      valid = false;
    }

    const answers = question.answers ?? [];
    if (answers.length < FIELD_LIMITS.MIN_ANSWERS) {
      qErr.answersGeneral = t("EDUCATION_PAGE.MIN_TWO_ANSWERS_REQUIRED");
      valid = false;
    } else if (!answers.some((answer: any) => answer.isCorrect)) {
      qErr.answersGeneral = t("EDUCATION_PAGE.AT_LEAST_ONE_CORRECT_ANSWER");
      valid = false;
    }

    qErr.answers = answers.map((answer: any) => {
      const answerErr: AnswerFieldErrors = {};
      const trimmedAnswer = (answer.text ?? "").trim();

      if (!trimmedAnswer) {
        answerErr.text = t("EDUCATION_PAGE.ANSWER_TEXT_REQUIRED");
        valid = false;
      } else if (trimmedAnswer.length > FIELD_LIMITS.ANSWER) {
        answerErr.text = t("EDUCATION_PAGE.ANSWER_TEXT_TOO_LONG", {
          max: FIELD_LIMITS.ANSWER,
        });
        valid = false;
      }

      return answerErr;
    });

    return qErr;
  });

  return {
    valid,
    errors: { questions: questionErrors },
  };
};

const QuestionsFormComponent = ({
  checkpointId,
  questions: existingQuestions,
  loading,
  onQuestionsSaved,
}: {
  checkpointId: string;
  questions: any[];
  loading: boolean;
  onQuestionsSaved: () => void;
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState<any[]>([emptyQuestionForm()]);
  const [errors, setErrors] = useState<QuestionsFormErrors>({ questions: [] });
  const [submitted, setSubmitted] = useState(false);

  const addQuestionAt = useCallback((index: number) => {
    setQuestions((prev) => {
      const next = [...prev];
      next.splice(index, 0, emptyQuestionForm());
      return next;
    });
  }, []);

  const addQuestionAtEnd = useCallback(() => {
    setQuestions((prev) => [...prev, emptyQuestionForm()]);
  }, []);

  const removeQuestionAt = useCallback((questionIndex: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== questionIndex));
    setErrors((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== questionIndex),
    }));
  }, []);

  const updateQuestionAt = useCallback(
    (questionIndex: number, key: string, value: string | boolean) => {
      setQuestions((prev) =>
        prev.map((question, i) =>
          i === questionIndex ? { ...question, [key]: value } : question,
        ),
      );
      if (key === "question") {
        setErrors((prev) => {
          const next = { ...prev, questions: [...prev.questions] };
          const qErr =
            next.questions[questionIndex] ?? createEmptyQuestionErrors();
          qErr.question = undefined;
          next.questions[questionIndex] = qErr;
          return next;
        });
      }
    },
    [],
  );

  const addAnswerAt = useCallback((questionIndex: number) => {
    setQuestions((prev) =>
      prev.map((question, i) =>
        i === questionIndex
          ? {
              ...question,
              answers: [...question.answers, emptyAnswerForm()],
            }
          : question,
      ),
    );
  }, []);

  const removeAnswerAt = useCallback(
    (questionIndex: number, answerIndex: number) => {
      const answerCount = questions[questionIndex]?.answers?.length ?? 0;
      if (answerCount <= FIELD_LIMITS.MIN_ANSWERS) {
        showToastMsg("error", t("EDUCATION_PAGE.MIN_TWO_ANSWERS_REQUIRED"));
        return;
      }

      setQuestions((prev) =>
        prev.map((question, i) =>
          i === questionIndex
            ? {
                ...question,
                answers: question.answers.filter(
                  (_: any, j: number) => j !== answerIndex,
                ),
              }
            : question,
        ),
      );
    },
    [questions, t],
  );

  const updateAnswerAt = useCallback(
    (questionIndex: number, answerIndex: number, value: string) => {
      setQuestions((prev) =>
        prev.map((question, i) =>
          i === questionIndex
            ? {
                ...question,
                answers: question.answers.map((answer: any, j: number) =>
                  j === answerIndex ? { ...answer, text: value } : answer,
                ),
              }
            : question,
        ),
      );
      setErrors((prev) => {
        const next = { ...prev, questions: [...prev.questions] };
        const qErr =
          next.questions[questionIndex] ?? createEmptyQuestionErrors();
        const answerErrs = [...(qErr.answers ?? [])];
        if (answerErrs[answerIndex]) {
          answerErrs[answerIndex] = {
            ...answerErrs[answerIndex],
            text: undefined,
          };
        }
        qErr.answers = answerErrs;
        next.questions[questionIndex] = qErr;
        return next;
      });
    },
    [],
  );

  const toggleCorrectAnswerAt = useCallback(
    (questionIndex: number, answerIndex: number) => {
      setQuestions((prev) =>
        prev.map((question, i) => {
          if (i !== questionIndex) return question;

          const nextAnswers = question.answers.map((answer: any, j: number) =>
            j === answerIndex
              ? { ...answer, isCorrect: !answer.isCorrect }
              : answer,
          );

          const hasCorrect = nextAnswers.some(
            (answer: any) => answer.isCorrect,
          );
          if (hasCorrect) {
            setErrors((prevErrors) => {
              const next = {
                ...prevErrors,
                questions: [...prevErrors.questions],
              };
              const qErr =
                next.questions[questionIndex] ?? createEmptyQuestionErrors();
              qErr.answersGeneral = undefined;
              next.questions[questionIndex] = qErr;
              return next;
            });
          }

          return { ...question, answers: nextAnswers };
        }),
      );
    },
    [],
  );

  const submit = useCallback(async () => {
    setSubmitted(true);
    const { valid, errors: validationErrors } = validateQuestionsForm(
      questions,
      t,
    );
    setErrors(validationErrors);

    if (!valid) {
      showToastMsg("error", t("EDUCATION_PAGE.PLEASE_FILL_ALL_FIELDS"));
      return;
    }

    try {
      setSaving(true);
      const payload = questions.map((question) => ({
        visibility: question.visibility,
        question: question.question.trim(),
        answers: question.answers.map((answer: any) => ({
          text: answer.text.trim(),
          isCorrect: answer.isCorrect,
        })),
      }));

      const response = await services.courses.addCheckpointQuestions(
        checkpointId,
        payload,
      );
      if (response && response.message === "SUCCESS") {
        showToastMsg(
          "success",
          t("EDUCATION_PAGE.QUESTIONS_ADDED_SUCCESSFULLY", {
            count: questions.length,
          }),
        );
        setSubmitted(false);
        setErrors({ questions: [] });
        onQuestionsSaved();
      } else {
        showPopupToastMsg(
          "error",
          response?.message || t("SOME_THING_WENT_WRONG"),
        );
      }
    } catch (error) {
      console.error(error);
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
  }, [checkpointId, questions, t, onQuestionsSaved]);

  useEffect(() => {
    if (existingQuestions && existingQuestions.length > 0) {
      setQuestions(existingQuestions);
    }
  }, [existingQuestions]);

  const showFormError = submitted && Boolean(errors.form);

  return (
    <Box className="global__box__content p-0 mb-5">
      <Box
        className="global__box__content__header"
        style={{ padding: "15px 2rem" }}
      >
        <Box
          className="info d-flex"
          style={{ gap: "1.9rem", alignItems: "center" }}
        >
          {loading ? (
            <Skeleton variant="text" width={250} height={20} />
          ) : (
            <Typography variant="h2" className="font-weight-bold">
              {t("EDUCATION_PAGE.QUESTIONS")}
            </Typography>
          )}
          {loading ? (
            <Skeleton variant="text" width={100} height={16} />
          ) : (
            <Typography variant="caption">
              {questions.length} {t("TOTAL")}
            </Typography>
          )}
        </Box>
        <Box className="global__box__content__action">
          {loading ? (
            <Skeleton variant="text" width={160} height={20} />
          ) : (
            <Typography variant="body1" style={{ color: "var(--hint-text)" }}>
              {t("EDUCATION_PAGE.ADD_ANOTHER_QUESTION")}
            </Typography>
          )}
          {loading ? (
            <Skeleton variant="text" width={120} height={35} />
          ) : (
            <AppButtonComponent
              variant="contained"
              color="primary"
              size="medium"
              onClick={addQuestionAtEnd}
            >
              {t("EDUCATION_PAGE.ADD_QUESTION")}
            </AppButtonComponent>
          )}
        </Box>
      </Box>

      {questions.map((question, questionIndex) => {
        const questionErrors =
          errors.questions[questionIndex] ?? createEmptyQuestionErrors();

        return (
          <Box
            key={questionIndex}
            className="global__box__content__body"
            style={{
              padding: "15px 2rem",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <Box className="question__actions">
              <span className="line"></span>
              <Box
                className="d-flex align-items-center justify-content-between"
                style={{ gap: "1.9rem" }}
              >
                <span
                  className="icon-wrap"
                  onClick={() => removeQuestionAt(questionIndex)}
                >
                  <img src={icons.delete} alt="trash" />
                </span>
                <span
                  className="icon-wrap"
                  onClick={() => addQuestionAt(questionIndex)}
                >
                  <img src={icons.PLUS} alt="plus" />
                </span>
              </Box>
              <span className="line"></span>
            </Box>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1.9rem",
              }}
            >
              <Box className="global__box__content question__form p-0">
                <Box className="question__title d-flex align-items-center justify-content-between">
                  <Typography variant="h3" className="font-weight-bold">
                    {t("QUESTION")} {questionIndex + 1} {t("OF")}{" "}
                    {questions.length}
                  </Typography>
                  <AppSwitchComponent
                    label={t("EDUCATION_PAGE.MAKE_QUESTION_VISIBLE")}
                    checked={question.visibility === "PUBLIC"}
                    labelColor="var(--hint-text)"
                    onChange={(e: any) => {
                      const { checked } = e.target;
                      updateQuestionAt(
                        questionIndex,
                        "visibility",
                        checked ? "PUBLIC" : "ADMIN_ONLY",
                      );
                    }}
                  />
                </Box>
                <Box className="question__input standard_text_input">
                  <TextField
                    className="w-100"
                    variant="standard"
                    placeholder={t("EDUCATION_PAGE.SUBTITLE")}
                    multiline
                    name="brief"
                    rows={4}
                    value={question.question}
                    onChange={(e) =>
                      updateQuestionAt(
                        questionIndex,
                        "question",
                        e.target.value,
                      )
                    }
                    error={submitted && Boolean(questionErrors.question)}
                    inputProps={{
                      maxLength: FIELD_LIMITS.QUESTION,
                    }}
                  />
                  <Box className="hint d-flex align-items-center justify-content-between">
                    <Typography variant="caption" color="error">
                      {submitted ? questionErrors.question : ""}
                    </Typography>
                    <Typography
                      variant="body1"
                      className="px-2 text-right"
                      style={{ flex: "auto" }}
                    >
                      {(question.question ?? "").length}/{FIELD_LIMITS.QUESTION}
                    </Typography>
                  </Box>
                </Box>
                <Box className="question__answers">
                  <Box className="mb-2">
                    <Typography variant="h4" className="font-weight-bold">
                      {t("EDUCATION_PAGE.ANSWERS")}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {t("EDUCATION_PAGE.ANSWERS_DESCRIPTION")}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {t("EDUCATION_PAGE.MAX_6_ANSWERS")}
                    </Typography>
                    {submitted && questionErrors.answersGeneral && (
                      <Typography
                        variant="caption"
                        color="error"
                        className="d-block mt-1"
                      >
                        {questionErrors.answersGeneral}
                      </Typography>
                    )}
                  </Box>
                  {question.answers.map((answer: any, answerIndex: number) => {
                    const answerError =
                      questionErrors.answers[answerIndex]?.text;

                    return (
                      <Box className="question__answer" key={answerIndex}>
                        <AppCheckboxComponent
                          checked={answer.isCorrect}
                          onChange={() =>
                            toggleCorrectAnswerAt(questionIndex, answerIndex)
                          }
                        />
                        <Box className="standard_text_input">
                          <TextField
                            className="w-100"
                            variant="standard"
                            placeholder={t("ANSWER")}
                            name="answer"
                            value={answer.text}
                            onChange={(e) =>
                              updateAnswerAt(
                                questionIndex,
                                answerIndex,
                                e.target.value,
                              )
                            }
                            error={submitted && Boolean(answerError)}
                            inputProps={{
                              maxLength: FIELD_LIMITS.ANSWER,
                            }}
                          />
                          <Box className="hint d-flex align-items-center justify-content-between">
                            <Typography variant="caption" color="error">
                              {submitted ? answerError : ""}
                            </Typography>
                          </Box>
                        </Box>
                        <span
                          className="icon-wrap"
                          onClick={() =>
                            removeAnswerAt(questionIndex, answerIndex)
                          }
                        >
                          <img src={icons.delete} alt="trash" />
                        </span>
                      </Box>
                    );
                  })}
                </Box>
                <Box className="question__answers__actions">
                  <span className="icon-wrap">
                    <img
                      src={icons.delete}
                      alt="trash"
                      onClick={() => removeQuestionAt(questionIndex)}
                    />
                  </span>
                  <span
                    className="icon-wrap"
                    style={{
                      opacity: question.answers.length >= 6 ? 0.5 : 1,
                      cursor:
                        question.answers.length >= 6
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={() => {
                      if (question.answers.length < 6) {
                        addAnswerAt(questionIndex);
                      } else {
                        showToastMsg(
                          "error",
                          t("EDUCATION_PAGE.MAX_6_ANSWERS"),
                        );
                      }
                    }}
                  >
                    <img src={icons.PLUS} alt="plus" />
                  </span>
                </Box>
              </Box>
              <Box className="question__bind" style={{ padding: "15px" }}>
                <Typography
                  variant="h3"
                  className="font-weight-bold mb-4 text-center"
                >
                  {t("QUESTION")} {questionIndex + 1} {t("OF")}{" "}
                  {questions.length}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {question.question}
                </Typography>
                <Box className="question__answers">
                  {question.answers.map((answer: any, index: number) => (
                    <Box key={index}>
                      {answer.text && (
                        <AppCheckboxComponent
                          label={answer.text}
                          checked={answer.isCorrect}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}

      <Box className="question__actions" style={{ margin: "1.9rem 0" }}>
        <span className="line"></span>
        <Box
          className="d-flex align-items-center justify-content-between"
          style={{ gap: "1.9rem" }}
        >
          <span className="icon-wrap" onClick={addQuestionAtEnd}>
            <img src={icons.PLUS} alt="plus" />
          </span>
        </Box>
        <span className="line"></span>
      </Box>

      {showFormError && (
        <Box style={{ padding: "0 2rem", textAlign: "end" }}>
          <Typography variant="caption" color="error">
            {errors.form}
          </Typography>
        </Box>
      )}

      <Box
        className="global__box__content__footer"
        style={{ justifyContent: "flex-end", padding: "15px 2rem" }}
      >
        <AppButtonComponent
          variant="outlined"
          color="secondary"
          disabled={saving}
          onClick={() => {
            setQuestions(existingQuestions);
            setErrors({ questions: [] });
            setSubmitted(false);
          }}
        >
          {t("CANCEL")}
        </AppButtonComponent>
        <AppButtonComponent
          variant="contained"
          color="primary"
          onClick={submit}
          disabled={saving}
        >
          {t("SAVE")}
        </AppButtonComponent>
      </Box>
    </Box>
  );
};

export default QuestionsFormComponent;
